import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api, formatApiError } from "../../lib/api";
import { DEFAULT_CATEGORIES } from "../../lib/constants";
import PublicHeader from "../../components/PublicHeader";
import SiteFooter from "../../components/SiteFooter";
import CoeditorGate from "../../components/CoeditorGate";
import { toast } from "sonner";
import { X, PlusCircle, ArrowRight } from "lucide-react";

const newRow = (category) => ({
  vendor_type: category,
  business_name: "",
  website: "",
  instagram: "",
  email: "",
  contact_name: "",
  vendor_id: null,
});

export default function VendorEntry() {
  const navigate = useNavigate();
  const { id } = useParams();
  const [event, setEvent] = useState(null);
  const [categories, setCategories] = useState(DEFAULT_CATEGORIES);
  const [rows, setRows] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState("");
  const [suggestions, setSuggestions] = useState({}); // rowIdx -> [vendor]
  const [openSuggestionFor, setOpenSuggestionFor] = useState(null);
  const [newCatName, setNewCatName] = useState("");
  const [showNewCat, setShowNewCat] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [meta, ev, draft] = await Promise.all([
          api.get("/meta/categories"),
          api.get(`/events/${id}/summary`).catch(() => null),
          api.get(`/events/${id}/draft`).catch(() => null),
        ]);
        let cats = meta.data.categories || DEFAULT_CATEGORIES;
        setEvent(ev?.data || draft?.data || null);
        const existing = draft?.data?.vendors || [];
        // Prefill previously saved credits (resume / edit)
        const extraCats = [...new Set(existing.map((v) => v.vendor_type).filter((t) => t && !cats.includes(t)))];
        if (extraCats.length) cats = [...cats, ...extraCats];
        cats = [...cats].sort((a, b) => a.localeCompare(b, undefined, { sensitivity: "base" }));
        setCategories(cats);
        const baseRows = cats.map((c) => newRow(c));
        existing.forEach((v, i) => {
          baseRows.push({
            vendor_type: v.vendor_type || "",
            business_name: v.business_name || "",
            website: v.website || "",
            instagram: v.instagram || "",
            email: v.email || "",
            contact_name: v.contact_name || "",
            vendor_id: v.vendor_id || null,
            sort_order: i,
          });
        });
        setRows(baseRows);
      } catch {
        setRows(DEFAULT_CATEGORIES.map((c) => newRow(c)));
      }
    })();
  }, [id]);

  const pub = event?.publication === "dwt" ? "dwt" : event?.publication === "dwh" ? "dwh" : null;
  useEffect(() => {
    if (pub) document.documentElement.dataset.pub = pub;
    return () => { delete document.documentElement.dataset.pub; };
  }, [pub]);

  const grouped = useMemo(() => {
    const map = new Map();
    categories.forEach((c) => map.set(c, []));
    rows.forEach((r, idx) => {
      if (!map.has(r.vendor_type)) map.set(r.vendor_type, []);
      map.get(r.vendor_type).push({ ...r, _idx: idx });
    });
    return map;
  }, [rows, categories]);

  const updateRow = (idx, field, value) =>
    setRows((arr) => arr.map((r, i) => (i === idx ? { ...r, [field]: value } : r)));

  const addRow = (category) =>
    setRows((arr) => [...arr, newRow(category)]);

  const removeRow = (idx) =>
    setRows((arr) => arr.filter((_, i) => i !== idx));

  const addCustomCategory = () => {
    const name = newCatName.trim();
    if (!name) return;
    setCategories((c) => [...c, name]);
    setRows((arr) => [...arr, newRow(name)]);
    setNewCatName("");
    setShowNewCat(false);
  };

  const handleSearch = async (idx, q) => {
    if (q.length < 2) {
      setSuggestions((s) => ({ ...s, [idx]: [] }));
      setOpenSuggestionFor(null);
      return;
    }
    try {
      const { data } = await api.get(`/vendors/search?q=${encodeURIComponent(q)}`);
      setSuggestions((s) => ({ ...s, [idx]: data.results || [] }));
      setOpenSuggestionFor(idx);
    } catch { /* noop */ }
  };

  const pickSuggestion = (idx, v) => {
    setRows((arr) =>
      arr.map((r, i) =>
        i === idx
          ? {
              ...r,
              business_name: v.name || "",
              website: v.website || "",
              instagram: v.instagram || "",
              email: v.email || "",
              contact_name: v.contact_name || "",
              vendor_id: v.id || null,
            }
          : r
      )
    );
    setOpenSuggestionFor(null);
  };

  const submit = async () => {
    const filled = rows.filter((r) => r.business_name.trim());
    if (filled.length === 0) {
      const message = "Please add at least one vendor.";
      setFormError(message);
      toast.error(message);
      return;
    }
    setFormError("");
    setSubmitting(true);
    try {
      await api.post(`/events/${id}/vendors`, { vendors: filled });
      navigate(`/create-event/${id}/story`);
    } catch (err) {
      const message = formatApiError(err, "We could not save the vendor credits.");
      setFormError(message);
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  };

  const locked = event && event.editable === false;

  return (
    <div className="min-h-screen bg-[#f5f3ed] flex flex-col" data-testid="vendor-entry-page" data-pub={pub || "neutral"}>
      <PublicHeader pub={pub} />

      <CoeditorGate eventId={id}>
      <main className="w-full max-w-3xl mx-auto px-6 sm:px-10 pt-12 pb-16">
        {formError && <div role="alert" className="mb-6 border border-[#b35c5c] bg-white p-4 text-sm text-[#9b3f3f]" data-testid="vendors-submit-error">{formError}</div>}
        {locked && (
          <div className="bg-white border border-[#c19872] rounded-xl p-4 text-sm text-black mb-8" data-testid="locked-banner">
            This submission is with our editorial team and can no longer be edited.
          </div>
        )}

        <div className="mb-10">
          <p className="btw-step-label">Vendor credits</p>
          <h1 className="text-3xl sm:text-4xl font-bold leading-tight mb-3" data-testid="vendors-heading">
            Who brought the day to life?
          </h1>
          <p className="text-sm text-black leading-relaxed max-w-xl">
            Fill what you remember. Start typing a business name and we'll try to find
            them. Skip anything that doesn't apply, add additional vendors as you need.
          </p>
        </div>

        {Array.from(grouped.entries()).map(([cat, items]) => (
          <div key={cat} className="mb-10" data-testid={`category-${cat}`}>
            <div className="btw-section !mb-3">
            <h2 className="text-center text-lg font-bold underline underline-offset-4 mb-8">{cat}</h2>

            <div className="space-y-10">
              {items.map((r) => (
                <div key={r._idx} className="relative" data-testid={`vendor-row-${r._idx}`}>
                  {items.length > 1 && (
                    <button
                      type="button"
                      aria-label={`Remove this ${cat}`}
                      onClick={() => removeRow(r._idx)}
                      data-testid={`remove-vendor-${r._idx}`}
                      className="absolute -top-2 right-0 text-black hover:text-[#b35c5c]"
                    >
                      <X size={16} />
                    </button>
                  )}
                  <div className="grid sm:grid-cols-3 gap-x-6 gap-y-5">
                    <div className="relative">
                      <label className="text-xs font-bold block mb-1">Business Name</label>
                      <input
                        data-testid={`vendor-name-${r._idx}`}
                        className="btw-input"
                        value={r.business_name}
                        onChange={(e) => {
                          updateRow(r._idx, "business_name", e.target.value);
                          updateRow(r._idx, "vendor_id", null);
                          handleSearch(r._idx, e.target.value);
                        }}
                        onFocus={() =>
                          r.business_name.length >= 2 && setOpenSuggestionFor(r._idx)
                        }
                        onBlur={() => setTimeout(() => setOpenSuggestionFor(null), 150)}
                       aria-label="Business Name"/>
                      {openSuggestionFor === r._idx &&
                        (suggestions[r._idx] || []).length > 0 && (
                          <div className="absolute z-20 left-0 right-0 mt-1 bg-white border border-[#c19872] rounded-lg shadow-sm overflow-hidden">
                            {suggestions[r._idx].map((s) => (
                              <button
                                key={s.id}
                                type="button"
                                onMouseDown={() => pickSuggestion(r._idx, s)}
                                onClick={() => pickSuggestion(r._idx, s)}
                                data-testid={`suggestion-${s.id}`}
                                className="w-full text-left px-3 py-2 hover:bg-[#f5f3ed] text-sm border-b last:border-b-0 border-[#d8cec0]"
                              >
                                <div className="font-medium">{s.name}</div>
                                {s.website && (
                                  <div className="text-xs text-[#5c5852]">{s.website}</div>
                                )}
                              </button>
                            ))}
                          </div>
                        )}
                    </div>
                    <div>
                      <label className="text-xs font-bold block mb-1">Website</label>
                      <input
                        data-testid={`vendor-website-${r._idx}`}
                        className="btw-input"
                        placeholder="https://www…."
                        value={r.website}
                        onChange={(e) => updateRow(r._idx, "website", e.target.value)}
                       aria-label="Website"/>
                    </div>
                    <div>
                      <label className="text-xs font-bold block mb-1">Instagram</label>
                      <div className="btw-ig">
                        <span className="text-black">@</span>
                        <input
                          data-testid={`vendor-instagram-${r._idx}`}
                          aria-label="Instagram"
                          className="btw-input pl-1"
                          value={r.instagram}
                          onChange={(e) =>
                            updateRow(r._idx, "instagram", e.target.value.replace(/^@/, ""))
                          }
                        />
                      </div>
                    </div>
                    <div>
                      <label className="text-xs font-bold block mb-1">Contact Name</label>
                      <input
                        data-testid={`vendor-contact-${r._idx}`}
                        className="btw-input"
                        placeholder="Who did you chat with?"
                        value={r.contact_name}
                        onChange={(e) => updateRow(r._idx, "contact_name", e.target.value)}
                       aria-label="Contact Name"/>
                    </div>
                    <div className="sm:col-span-2">
                      <label className="text-xs font-bold block mb-1">Email</label>
                      <input
                        data-testid={`vendor-email-${r._idx}`}
                        type="email"
                        className="btw-input"
                        placeholder="creative@weddingpro.com"
                        value={r.email}
                        onChange={(e) => updateRow(r._idx, "email", e.target.value)}
                       aria-label="Email"/>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            </div>
            <button
              type="button"
              onClick={() => addRow(cat)}
              data-testid={`add-${cat}`}
              className="btw-btn-outline"
            >
              <PlusCircle size={15} /> Add another {cat}
            </button>
          </div>
        ))}

        {showNewCat ? (
          <div className="mb-10 flex gap-2 items-center max-w-md" data-testid="custom-category-input-row">
            <input
              autoFocus
              aria-label="New category name"
              data-testid="custom-category-input"
              value={newCatName}
              onChange={(e) => setNewCatName(e.target.value)}
              placeholder="Category name (e.g. Photo Booth)"
              className="flex-1 btw-input"
              onKeyDown={(e) => {
                if (e.key === "Enter") { e.preventDefault(); addCustomCategory(); }
                if (e.key === "Escape") { setShowNewCat(false); setNewCatName(""); }
              }}
            />
            <button
              type="button"
              onClick={addCustomCategory}
              data-testid="confirm-custom-category"
              className="btw-btn-outline"
            >
              Add
            </button>
            <button
              type="button"
              onClick={() => { setShowNewCat(false); setNewCatName(""); }}
              className="text-sm text-black hover:text-[#b35c5c]"
            >
              Cancel
            </button>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => setShowNewCat(true)}
            data-testid="add-custom-category"
            className="mb-10 btw-btn-outline"
          >
            <PlusCircle size={15} /> Add a vendor in another category
          </button>
        )}

        <div className="flex justify-end">
          <button type="button" onClick={submit} disabled={submitting || locked} data-testid="submit-vendors" className="btw-btn w-full sm:w-auto">
            <span>{submitting ? "Sending…" : "Continue to your story"}</span>
            <span className="btw-btn-arrow"><ArrowRight size={12} /></span>
          </button>
        </div>
      </main>
      </CoeditorGate>

      <SiteFooter pub={pub} />
    </div>
  );
}

